import React, { useState, useMemo } from 'react';
import { Calendar, CheckCircle, RefreshCw, MousePointerClick, MoreHorizontal, ArrowUpDown } from 'lucide-react';

const StatCard = ({ icon, title, value, delta, iconBgColor }) => (
  <div className="rounded-2xl border border-gray-200 bg-[#fafafa] p-3 md:p-4">
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-xl text-white"
          style={{ background: iconBgColor }}
        >
          {icon}
        </span>
        <div className="text-sm text-[#1D2035]">{title}</div>
      </div>
      <button className="w-8 h-8 grid place-items-center rounded-lg bg-white text-gray-500">
        <MoreHorizontal size={20} />
      </button>
    </div>
    <div className="mt-3 flex items-end justify-between">
      <div className="text-2xl font-semibold text-[#1D2035]">{value.toLocaleString()}</div>
      <div className="text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
          +{delta}%
        </span>
      </div>
    </div>
  </div>
);

const StatusPill = ({ status }) => {
  const isSuccess = status === "تم الارسال بنجاح";
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${
        isSuccess 
        ? "bg-green-50 text-green-700 border-green-200" 
        : "bg-red-50 text-red-700 border-red-200"
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isSuccess ? "bg-green-500" : "bg-red-500"}`}></span>
      {status}
    </span>
  );
};

export default function NotificationsPage() {
  const dir = "rtl";
  const [dateRange] = useState({ from: "يناير 2025", to: "يونيو 2025" });

  const stats = useMemo(() => [
    { title: "تم إرسالها بنجاح", value: 3056, delta: 4.9, icon: <CheckCircle size={20} />, color: "#059669" },
    { title: "تم فتحها", value: 1535, delta: 3.5, icon: <RefreshCw size={20} />, color: "#2563EB" },
    { title: "تم الضغط", value: 929, delta: 2.3, icon: <MousePointerClick size={20} />, color: "#7C3AED" },
  ], []);

  const notifications = useMemo(() => [
    { id: 1, title: "فتوى حول مدى خضوع الجهات التي تتمتع بالإع...", date: "2025/11/04", number: "1057", discountCode: "رابط خارجي", type: "اعلان جديد", status: "تم الارسال بنجاح" },
    { id: 2, title: "فتوى حول مدى خضوع الجهات التي تتمتع بالإع...", date: "2025/11/04", number: "4324023", discountCode: "رابط خارجي", type: "تحديث جديد", status: "تم الارسال بنجاح" },
    { id: 3, title: "فتوى حول مدى خضوع الجهات التي تتمتع بالإع...", date: "2025/11/04", number: "4324023", discountCode: "#A50", type: "عرض - خصم", status: "تم الارسال بنجاح" },
    { id: 4, title: "فتوى حول مدى خضوع الجهات التي تتمتع بالإع...", date: "2025/11/04", number: "4324023", discountCode: "رابط خارجي", type: "اعلان جديد", status: "تم الارسال بنجاح" },
    { id: 5, title: "فتوى حول مدى خضوع الجهات التي تتمتع بالإع...", date: "2025/11/04", number: "4324023", discountCode: "رابط خارجي", type: "اعلان جديد", status: "تم الارسال بنجاح" },
    { id: 6, title: "فتوى حول مدى خضوع الجهات التي تتمتع بالإع...", date: "2025/11/04", number: "4324023", discountCode: "رابط خارجي", type: "اعلان جديد", status: "فشل الارسال" },
    { id: 7, title: "فتوى حول مدى خضوع الجهات التي تتمتع بالإع...", date: "2025/11/04", number: "4324023", discountCode: "#A70", type: "عرض - خصم", status: "تم الارسال بنجاح" },
    { id: 8, title: "فتوى حول مدى خضوع الجهات التي تتمتع بالإع...", date: "2025/11/04", number: "4324023", discountCode: "#A90", type: "عرض - خصم", status: "تم الارسال بنجاح" },
  ], []);

  return (
    <div className="min-h-screen p-4 md:p-6 bg-white" dir={dir}>
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 md:gap-3">
            <button className="px-4 py-2.5 rounded-lg text-white bg-[var(--Primary-Primary)] shadow-sm text-sm md:text-base">
              إرسال إشعار جديد
            </button>
            <button className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-[#1D2035] flex items-center gap-2">
              <ArrowUpDown size={16} className="text-gray-500" />
              من الاحدث الى الاقدم
            </button>
            <button className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-[#1D2035] flex items-center gap-2">
              <Calendar size={16} className="text-gray-500" />
              {dateRange.from} - {dateRange.to}
            </button>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">إجمالي الإشعارات المرسلة</div>
            <div className="text-3xl md:text-4xl font-bold text-[#1D2035]">5,523</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mt-4">
          {stats.map((s, idx) => (
            <StatCard key={idx} icon={s.icon} title={s.title} value={s.value} delta={s.delta} iconBgColor={s.color} />
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="mt-5 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 md:px-6 py-3">
          <h3 className="font-semibold text-[#1D2035]">الإشعارات</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-gray-600 bg-[#fafafa]">
              <tr>
                <th className="p-3 w-10"><input type="checkbox" className="rounded border-gray-300" /></th>
                <th className="p-3 text-right font-medium">عنوان الإشعار</th>
                <th className="p-3 text-right font-medium">تاريخ الإرسال</th>
                <th className="p-3 text-right font-medium">رقم</th>
                <th className="p-3 text-right font-medium">كود الخصم</th>
                <th className="p-3 text-right font-medium">نوع الإشعار</th>
                <th className="p-3 text-right font-medium">حالة الإشعار</th>
                <th className="p-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((n) => (
                <tr key={n.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="p-3 text-center"><input type="checkbox" className="rounded border-gray-300" /></td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gray-100"></div>
                      <div className="truncate max-w-[420px] text-[#1D2035]">{n.title}</div>
                    </div>
                  </td>
                  <td className="p-3 whitespace-nowrap text-gray-700">{n.date}</td>
                  <td className="p-3 whitespace-nowrap text-gray-700">{n.number}</td>
                  <td className="p-3 whitespace-nowrap text-gray-700">{n.discountCode}</td>
                  <td className="p-3 whitespace-nowrap text-gray-700">{n.type}</td>
                  <td className="p-3"><StatusPill status={n.status} /></td>
                  <td className="p-3 text-gray-400">
                    <button className="w-7 h-7 grid place-items-center rounded-md border border-gray-200">…</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 md:px-6 py-3 text-sm text-gray-600">
          <div>الصفحة 1 من 90</div>
          <div className="flex items-center gap-2">
            <button className="px-2 py-1 rounded-md border border-gray-200">«</button>
            {[1, 2, 3].map((n) => (
              <button key={n} className={`px-2.5 py-1 rounded-md border ${n === 1 ? "bg-gray-900 text-white border-gray-900" : "border-gray-200"}`}>
                {n}
              </button>
            ))}
            <span className="px-2">…</span>
            <button className="px-2.5 py-1 rounded-md border border-gray-200">90</button>
            <button className="px-2 py-1 rounded-md border border-gray-200">»</button>
          </div>
        </div>
      </div>
    </div>
  );
}
